// lib/realtimeVoice.ts — WebRTC driver for Azure AI Foundry realtime voice (spec §6.5).
// Fully injectable (peer-connection factory, mic, fetch, session) so it's unit-testable
// without a real browser stack. The ephemeral key is short-lived and minted server-side.

export interface RealtimeSession {
  ephemeralKey: string;
  webrtcUrl: string;
  model: string;
}

export interface RealtimeVoiceDeps {
  startSession: () => Promise<RealtimeSession>;
  createPeerConnection: () => RTCPeerConnection;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  fetchFn: typeof fetch;
  attachRemoteAudio: (stream: MediaStream) => void;
  onTranscript?: (role: 'user' | 'assistant', text: string) => void;
  onState?: (state: RTCPeerConnectionState) => void;
  onToolCall?: (name: string, args: Record<string, unknown>) => Promise<string>;
}

export interface RealtimeVoiceHandle {
  stop: () => void;
}

/** Data-channel plumbing for executing a model tool call and replying over the channel. */
export interface RealtimeToolContext {
  onToolCall?: (name: string, args: Record<string, unknown>) => Promise<string>;
  send: (message: unknown) => void;
}

/** Parse a data-channel event: surface completed transcripts and run model tool calls. */
export async function handleRealtimeEvent(
  data: unknown,
  onTranscript?: (role: 'user' | 'assistant', text: string) => void,
  tools?: RealtimeToolContext,
): Promise<void> {
  if (typeof data !== 'string') return;
  let evt: {
    type?: string;
    transcript?: string;
    name?: string;
    call_id?: string;
    arguments?: string;
  };
  try {
    evt = JSON.parse(data);
  } catch {
    return;
  }
  if (evt.type === 'response.function_call_arguments.done') {
    if (!tools?.onToolCall || typeof evt.name !== 'string' || typeof evt.call_id !== 'string') {
      return;
    }
    let args: Record<string, unknown> = {};
    try {
      args = evt.arguments ? (JSON.parse(evt.arguments) as Record<string, unknown>) : {};
    } catch {
      args = {};
    }
    const output = await tools.onToolCall(evt.name, args);
    tools.send({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: evt.call_id, output },
    });
    tools.send({ type: 'response.create' });
    return;
  }
  if (!evt.transcript) return;
  if (evt.type === 'response.output_audio_transcript.done') {
    onTranscript?.('assistant', evt.transcript);
  } else if (evt.type === 'conversation.item.input_audio_transcription.completed') {
    onTranscript?.('user', evt.transcript);
  }
}

/** Open a live voice session: mint token → WebRTC offer/answer → stream. */
export async function startRealtimeVoice(deps: RealtimeVoiceDeps): Promise<RealtimeVoiceHandle> {
  const session = await deps.startSession();
  const pc = deps.createPeerConnection();

  pc.ontrack = (e) => {
    const stream = e.streams[0];
    if (stream) deps.attachRemoteAudio(stream);
  };
  pc.onconnectionstatechange = () => deps.onState?.(pc.connectionState);

  const media = await deps.getUserMedia({ audio: true });
  for (const track of media.getAudioTracks()) pc.addTrack(track, media);

  const channel = pc.createDataChannel('realtime-channel');
  channel.onmessage = (e) =>
    void handleRealtimeEvent(e.data, deps.onTranscript, {
      onToolCall: deps.onToolCall,
      send: (m) => channel.send(JSON.stringify(m)),
    });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const res = await deps.fetchFn(`${session.webrtcUrl}?webrtcfilter=on`, {
    method: 'POST',
    body: offer.sdp ?? '',
    headers: {
      Authorization: `Bearer ${session.ephemeralKey}`,
      'Content-Type': 'application/sdp',
    },
  });
  if (!res.ok) throw new Error(`Live voice failed to connect (${res.status})`);
  const answer = await res.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });

  return {
    stop: () => {
      channel.close();
      for (const track of media.getTracks()) track.stop();
      pc.close();
    },
  };
}
